import { synthesizeSpeech } from "./client";
import { PollyAudioPlayer } from "./player";
import { stripInlinePinyin } from "./sanitizeText";

const PREFETCH_WINDOW = 2;
/** Same as Polly demo: split on sentence punctuation only. */
const SENTENCE_SPLIT = /(?<=[。！？；.!?;\n])/;
/** Soft-split only when a single sentence is extremely long. */
const LONG_SENTENCE_CHARS = 800;
const SOFT_BREAK = /(?<=[，、,;:])/;
/** Must contain at least one letter/number (skip lone "." "。" etc.). */
const SPEAKABLE = /[\p{L}\p{N}]/u;

export function hasSpeakableContent(text) {
  return SPEAKABLE.test(text || "");
}

function softSplitLongSentence(sentence) {
  if (sentence.length <= LONG_SENTENCE_CHARS) {
    return [sentence];
  }

  const parts = sentence
    .split(SOFT_BREAK)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length <= 1) {
    return [sentence];
  }

  const chunks = [];
  let current = "";

  for (const part of parts) {
    if (!current) {
      current = part;
      continue;
    }
    if (current.length + part.length <= LONG_SENTENCE_CHARS) {
      current += part;
    } else {
      chunks.push(current);
      current = part;
    }
  }
  if (current) {
    chunks.push(current);
  }
  return chunks;
}

function splitSentences(text) {
  return (text || "")
    .split(SENTENCE_SPLIT)
    .map((sentence) => sentence.trim())
    .filter(hasSpeakableContent)
    .flatMap(softSplitLongSentence)
    .filter(hasSpeakableContent);
}

function isAbortError(error) {
  return error?.name === "AbortError";
}

/**
 * Sentence-level prefetch + sequential playback (same strategy as Polly demo).
 */
export class PollyTtsOrchestrator {
  constructor() {
    this.player = new PollyAudioPlayer();
    this.abortController = null;
  }

  unlockAutoplay() {
    return this.player.unlockAutoplay();
  }

  cancel() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.player.stop();
  }

  /**
   * @param {{ text: string, language: string, rate?: string, endpoint?: string, shouldContinue?: () => boolean }} options
   * @returns {Promise<'completed' | 'cancelled' | 'stopped' | 'empty'>}
   */
  async speak({ text, language, rate, endpoint, shouldContinue }) {
    this.cancel();

    const sentences = splitSentences(stripInlinePinyin(text || ""));
    if (sentences.length === 0) {
      return "empty";
    }

    this.abortController = new AbortController();
    const { signal } = this.abortController;
    const audioPromises = [];

    const enqueue = (index) => {
      audioPromises[index] = synthesizeSpeech(
        { text: sentences[index], language, rate, endpoint },
        signal
      ).catch((error) => {
        if (isAbortError(error)) {
          return null;
        }
        throw error;
      });
    };

    for (let i = 0; i < Math.min(PREFETCH_WINDOW, sentences.length); i += 1) {
      enqueue(i);
    }

    try {
      for (let i = 0; i < sentences.length; i += 1) {
        if (signal.aborted) {
          return "cancelled";
        }
        if (shouldContinue && !shouldContinue()) {
          this.cancel();
          return "stopped";
        }

        const blob = await audioPromises[i];
        if (!blob || signal.aborted) {
          return "cancelled";
        }

        const nextIndex = i + PREFETCH_WINDOW;
        if (nextIndex < sentences.length) {
          enqueue(nextIndex);
        }

        await this.player.playSegment(blob);
      }

      return signal.aborted ? "cancelled" : "completed";
    } catch (error) {
      if (isAbortError(error)) {
        return "cancelled";
      }
      throw error;
    }
  }
}
